import type { GmUserProfileViewProps } from '../types';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { GmStatusImage } from '@/components/entities/gamification/GmStatusImage';
import { ProfileEmailAndIdFields } from '@/components/entities/profile/ProfileEmailAndIdFields';
import { Icon } from '@/components/entities/Sprite';
import { Link } from '@/i18n/routing';
import { cn } from '@/utils/cn';

const DESKTOP_ANIMATION_MS = 200;

export const GmUserProfileDesktop = ({ statusName = '', statusImageSrc, user, isOpen, onOpenChange }: GmUserProfileViewProps) => {
  const [isDesktopPanelVisible, setIsDesktopPanelVisible] = useState(false);
  const [shouldRenderDesktopPanel, setShouldRenderDesktopPanel] = useState(false);
  const t = useTranslations();

  useEffect(() => {
    if (isOpen) {
      setShouldRenderDesktopPanel(true);
      const frameId = window.requestAnimationFrame(() => {
        setIsDesktopPanelVisible(true);
      });
      return () => window.cancelAnimationFrame(frameId);
    }

    setIsDesktopPanelVisible(false);

    if (!shouldRenderDesktopPanel) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setShouldRenderDesktopPanel(false);
    }, DESKTOP_ANIMATION_MS);

    return () => window.clearTimeout(timeoutId);
  }, [isOpen, shouldRenderDesktopPanel]);

  if (!shouldRenderDesktopPanel) {
    return null;
  }

  return (
    <div
      data-state={isOpen ? 'open' : 'closed'}
      className={cn(
        'absolute right-0 top-[calc(100%+1rem)] z-50 w-70 origin-top-right overflow-hidden rounded-2xl bg-(image:--gradients-popup-desktop) px-4 py-5 text-white shadow-card transition-[opacity,transform] duration-200 ease-out',
        isDesktopPanelVisible
          ? 'translate-y-0 scale-100 opacity-100'
          : 'pointer-events-none -translate-y-2 scale-95 opacity-0',
      )}
    >
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-frame-gradient opacity-20 blur-3xl" />
      <div className="relative z-1 flex flex-col items-center">
        {statusImageSrc && (
          <GmStatusImage
            variant="large"
            src={statusImageSrc}
            alt={statusName || 'Status icon'}
            className="mb-3 size-16 shrink-0 object-contain"
          />
        )}
        <div className="mb-5 text-center">
          <p className="text-xl gm-text-golden-2 font-hepta leading-[120%] tracking-normal font-bold uppercase">
            {statusName}
          </p>
          <p className="text-sm leading-5 text-c300">
            {t('gamification.scratch-card-statuses.status-label')}
          </p>
        </div>

        <ProfileEmailAndIdFields
          className="mb-5"
          userId={user?.id}
          email={user?.email}
          appearance="profileCard"
        />
        <div className="group relative w-full overflow-hidden rounded-full p-3">
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-header-gradient opacity-0 transition-opacity duration-500 ease-out group-hover:opacity-100"
          />
          <Link
            href="/profile"
            prefetch={false}
            onClick={() => onOpenChange(false)}
            className="relative z-1 flex w-full items-center gap-3 text-left text-white transition-colors duration-500 ease-out paragraph-s-medium group-hover:text-primary-3"
          >
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full">
              <Icon name="profile" className="size-6" />
            </span>
            {t('user-profile.information.my-profile')}
          </Link>
        </div>

      </div>
    </div>
  );
};
